import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapPin, Check, ChevronRight, Loader2, Clock } from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';
import API from '../services/api';
import { setBranch } from '../redux/slices/branchSlice';
import { isBranchOpen } from '../utils/branchUtils';

const BranchSelector = ({ onSelect }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedBranch } = useSelector((state) => state.branch);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await API.get('/locations');
        setBranches(res.data.data);
      } catch (err) {
        console.error('Error fetching branches:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, []);

  const handleSelect = (branch) => {
    dispatch(setBranch(branch));
    if (onSelect) onSelect(branch);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-12 space-y-4">
      <Loader2 className="animate-spin" size={32} style={{ color: theme.primary }} />
      <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Loading locations...</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3">
        {branches.map((branch) => {
          const isSelected = selectedBranch?._id === branch._id;
          const isOpen = isBranchOpen(branch.operatingHours);
          
          return (
            <button
              key={branch._id}
              onClick={() => handleSelect(branch)}
              className="flex items-center justify-between p-4 rounded-2xl border-2 transition-all text-left group"
              style={{ 
                backgroundColor: isSelected ? `${theme.primary}10` : theme.surface,
                borderColor: isSelected ? theme.primary : theme.border
              }}
            >
              <div className="flex items-center space-x-4">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                  style={{ backgroundColor: isSelected ? theme.primary : `${theme.text}10` }}
                >
                  <MapPin size={18} style={{ color: isSelected ? theme.background : theme.text }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-black uppercase tracking-tight" style={{ color: theme.text }}>
                      {branch.name}
                    </h4>
                    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase ${isOpen ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                      <Clock size={8} />
                      {isOpen ? t('common.open', 'Open') : t('common.closed', 'Closed')}
                    </div>
                  </div>
                  <p className="text-[10px] font-bold opacity-40 truncate max-w-[180px]">
                    {branch.address}
                  </p>
                </div>
              </div>
              
              {isSelected ? (
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
                  <Check size={14} style={{ color: theme.background }} />
                </div>
              ) : (
                <ChevronRight size={18} className="opacity-20 group-hover:translate-x-1 transition-transform" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BranchSelector;
